/**
 * Org Context for Exbabel Events 2.0
 * 
 * Provides organization-scoped state for the Events product.
 * Manages: current org selection, org list, membership info.
 * 
 * This is SEPARATE from AuthContext (which serves Exbabel Live).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const OrgContext = createContext(null);

// Vite proxies /api to the local backend in development. Staying on the page's
// origin avoids cross-origin preflights when .env.local points at a LAN address.
const API_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '');

export function OrgProvider({ children }) {
    const { user, session } = useAuth();
    const [orgs, setOrgs] = useState([]);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch user's organizations
    const fetchOrgs = useCallback(async () => {
        if (!session?.access_token) return;

        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/v2/orgs`, {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setOrgs(data);
                // Auto-select first org if none selected
                if (!currentOrg && data.length > 0) {
                    setCurrentOrg(data[0]);
                }
            }
        } catch (err) {
            console.error('[OrgContext] Failed to fetch orgs:', err);
        } finally {
            setLoading(false);
        }
    }, [session?.access_token, currentOrg]);

    useEffect(() => {
        if (user && session) {
            fetchOrgs();
        } else {
            setOrgs([]);
            setCurrentOrg(null);
            setLoading(false);
        }
    }, [user, session, fetchOrgs]);

    // Helper to make authenticated API calls scoped to the current org
    const orgFetch = useCallback(async (path, options = {}) => {
        if (!currentOrg || !session?.access_token) return null;

        const url = `${API_URL}/api/v2/orgs/${currentOrg.id}${path}`;
        const res = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                ...options.headers,
            },
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(err.error || `API error ${res.status}`);
        }
        return res.json();
    }, [currentOrg, session?.access_token]);

    const switchOrg = useCallback((org) => {
        setCurrentOrg(org);
    }, []);

    const createOrg = useCallback(async (orgData) => {
        if (!session?.access_token) return null;
        const res = await fetch(`${API_URL}/api/v2/orgs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(orgData),
        });
        if (!res.ok) throw new Error('Failed to create organization');
        const newOrg = await res.json();
        await fetchOrgs();
        setCurrentOrg({ ...newOrg, role: 'owner' });
        return newOrg;
    }, [session?.access_token, fetchOrgs]);

    const updateOrg = useCallback(async (updates) => {
        if (!currentOrg || !session?.access_token) return null;
        const res = await fetch(`${API_URL}/api/v2/orgs/${currentOrg.id}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(updates),
        });
        if (!res.ok) throw new Error('Failed to update organization');
        const updatedOrg = await res.json();
        await fetchOrgs();
        setCurrentOrg({ ...updatedOrg, role: currentOrg.role });
        return updatedOrg;
    }, [currentOrg, session?.access_token, fetchOrgs]);

    const value = {
        orgs,
        currentOrg,
        loading,
        switchOrg,
        createOrg,
        updateOrg,
        fetchOrgs,
        orgFetch,
        myRole: currentOrg?.role || null,
        isOrgAdmin: currentOrg?.role === 'owner' || currentOrg?.role === 'administrator',
        isProducer: ['owner', 'administrator', 'producer'].includes(currentOrg?.role),
    };

    return (
        <OrgContext.Provider value={value}>
            {children}
        </OrgContext.Provider>
    );
}

export function useOrg() {
    const context = useContext(OrgContext);
    if (!context) {
        throw new Error('useOrg must be used within an OrgProvider');
    }
    return context;
}
